<template>
  <UiPageComponent
    class="profile-products"
    :error="error"
    :pending="pending"
    @retry="refresh"
  >
    <div class="ui-wrapper">
      <div
        v-if="!productsCount"
        class="profile-products__empty"
      >
        <UIIcon name="products-empty" />
        <div class="profile-products__empty-text">
          <template v-if="isMyProfile">
            <strong class="profile-products__empty-title">
              {{ $t('common.noPublishedProducts') }}
            </strong>

            <i18n-t
              keypath="profile.profileId.indexPage.empty.text"
              tag="span"
            >
              <template #br>
                <br>
              </template>
            </i18n-t>

            <UIButton
              class="profile-products__empty-button"
              type="black"
              size="m"
              href="/publication"
            >
              {{ $t('common.saleProduct') }}
            </UIButton>
          </template>

          <template v-else>
            {{ $t('profile.profileId.indexPage.noProducts') }}
          </template>
        </div>
      </div>

      <template v-else>
        <div class="catalog-component">
          <div class="catalog-component__wrapper">
            <div class="catalog-component__wrapper__categories">
              <client-only>
                <FiltersCategoriesTreeCatalog
                  v-if="isDesktop"
                  where-are-we="catalog"
                  show-all-categories
                />
              </client-only>
            </div>

            <div class="catalog-component__wrapper__filters__products">
              <client-only>
                <CatalogFiltersBadgesSorting
                  v-if="isDesktop"
                  where-are-we="catalog"
                  :profile-id="profileId"
                />

                <CatalogFiltersMobileBadges
                  v-else
                  v-model:view-one-column="viewOneColumn"
                  :profile-id="profileId"
                />
              </client-only>

              <CatalogProductList
                :products="data?.items"
                :total-pages="data?.totalPages"
                :page="page"
                :loading="loading"
                :view-one-column="viewOneColumn"
                @click-more="loadMore"
                @click-page="goToPage"
              />
            </div>
          </div>
        </div>

        <client-only>
          <template v-if="!isDesktop">
            <button
              class="filters-mobile-show"
              @click="showFiltersMobile = true"
            >
              <UIIcon name="settings" />

              <span>{{ $t('profile.profileId.indexPage.filter') }}</span>
            </button>

            <CatalogFiltersMobile
              v-if="showFiltersMobile"
              where-are-we="catalog"
              show-all-categories
              :profile-id="profileId"
              @close="showFiltersMobile = false"
            />
          </template>
        </client-only>
      </template>
    </div>
  </UiPageComponent>
</template>

<script lang="ts" setup>
import { RouteLocationNormalizedLoaded } from 'vue-router';

import { Catalog } from '~/restAPI/Catalog';
import { useApi } from '~/restAPI';
import { mappedQueryParams } from '~/utils/api';
import { PublicProfileDTO } from '~/restAPI/data-contracts';
import CatalogFiltersMobile from '~/components/catalog/CatalogFiltersMobile/CatalogFiltersMobile.vue';
import CatalogFiltersMobileBadges from '~/components/catalog/CatalogFiltersMobile/CatalogFiltersMobileBadges.vue';

const { createApiInstance } = useApi();
const catalogApi = createApiInstance(Catalog);
const cancelGetCatalog = 'cancelGetCatalog';
const schemaOrg = useSchemaOrg();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

definePageMeta({
  scrollToTopFunc: (to: RouteLocationNormalizedLoaded, from: RouteLocationNormalizedLoaded) => {
    const isProfile = to.params.profileId === from.params.profileId;

    return !isProfile;
  },
});

useHead({
  title: `${t('common.profile')} – ${t('common.OskellyOnlineStore')}`,
  script: [
    {
      type: 'application/ld+json',
      children: schemaOrg,
    },
  ],
});

const props = defineProps<{
  profile: PublicProfileDTO
}>();

const { isDesktop } = useUiBreakpoints();

const loadingMore = ref(false);
const loading = ref(false);
const viewOneColumn = ref(false);
const showFiltersMobile = ref(false);

const page = computed(() => Number(route.query?.page) || 1);
const profileId = computed(() => props.profile.id!);
const productsCount = computed(() => props.profile.productsCount);
const isMyProfile = computed(() => props.profile.isMyProfile);

const {
  data, pending, error, refresh,
} = useLazyAsyncData('loadProfileProductsPage', request);

function request() {
  if (!productsCount.value) return Promise.resolve(null);

  return catalogApi.getCatalogUsingGet({
    ...mappedQueryParams(route),
    sellerId: profileId.value,
    page: page.value,
    pageLength: 60,
  }, { format: 'json', cancelToken: cancelGetCatalog })
    .then((res) => res?.data?.data);
}

async function goToPage(pageNumber: number): Promise<void> {
  const query = { ...route.query };

  if (pageNumber === 1) {
    delete query.page;
    await router.replace({ query });
    return;
  }

  query.page = `${pageNumber}`;
  await router.push({ query });
}

async function loadMore(): Promise<void> {
  loadingMore.value = true;

  await goToPage(page.value + 1);
}

async function loadAndUpdateCatalog() {
  loading.value = true;

  try {
    const catalog = await request();

    if (!catalog?.items) return;

    if (loadingMore.value) {
      data.value?.items?.push(...catalog.items);
      loadingMore.value = false;
    } else {
      data.value = catalog;
    }
  } catch (err) {
    error.value = err;
  } finally {
    loading.value = false;
  }
}

watch(() => route.query, (newQuery, prevQuery) => {
  if (!Object.keys({ ...newQuery, ...prevQuery }).length) return;

  if (newQuery.page && (newQuery.page === prevQuery.page)) {
    goToPage(1);
    return;
  }

  loadAndUpdateCatalog();
});

onUnmounted(() => {
  if (pending.value || loading.value) {
    catalogApi.abortRequest(cancelGetCatalog);
  }
});
</script>

<style scoped lang="scss">
.profile-products {
  :deep(.ui-loader-block) {
    padding-top: 80px;
  }

  &__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 80px 0;
  }

  &__empty-text {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    font-size: 12px;
    color: #828282;
    line-height: 16px;
    margin-top: 12px;
    text-align: center;
  }

  &__empty-title {
    color: #333;
    font-size: 14px;
    margin-top: 12px;
    margin-bottom: 8px;
  }

  &__empty-button {
    width: 260px;
    margin-top: 24px;
  }
}

.filters-mobile-show {
  display: none;

  @media (max-width: 1024px) {
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    left: 50%;
    bottom: 75px;
    transform: translate(-50%);
    width: 248px;
    height: 45px;
    color: #333;
    text-align: center;
    font-size: 14px;
    background: #fff;
    border-radius: 2px;
    z-index: 9;
    box-shadow: 0 5px 13px rgb(0 0 0 / 20%);

    span {
      margin-left: 8px;
      font-size: 16px;
      font-weight: 500;
    }
  }
}

@tailwind components;

@layer components {
  .catalog-component {
    @apply flex flex-col items-center pb-10;

    &__title {
      @apply text-[24px] mt-3 uppercase tracking-wide lg:text-[38px];
      font-family: 'Playfair Display', serif;
    }

    &__amount {
      @apply text-sm text-[#BDBDBD];
    }

    &__wrapper {
      @apply flex w-full justify-between gap-12 mt-4;

      &__categories {
        @apply hidden md:flex flex-shrink-0 w-[245px];
      }

      &__filters__products {
        @apply flex flex-col w-full;
      }
    }
  }
}
</style>
